<template>
    <!-- *********************************************
    *                Top Informations                *
    ********************************************** -->
    <text-blocks-operation-info v-if="responseData" :params="operationInfoParams" />
    <!-- *********************************************
    *                  Account Block                 *
    ********************************************** -->
    <divider-generic
    icon="account-balance"
    dividerSize="medium"
    :title="t('transfers.periodic.edit.step3.accountTitle')"/>
    <!-- Origin Account Text Block -->
    <text-block-generic
    :params="originAccountTextBlockParams"
    :mask="ACCOUNT_NUMBER"/>
    <!-- *********************************************
    *             Beneficiary Info Block             *
    ********************************************** -->
    <divider-generic
    icon="person"
    dividerSize="medium"
    :title="t('transfers.periodic.edit.step3.beneficiaryTitle')"/>
    <!-- Destination Account Text Block -->
    <text-block-generic
    :params="destinationAccountTextBlockParams"
    :mask="ACCOUNT_NUMBER"/>
    <!-- Beneficiary Text Block -->
    <text-block-generic
    :params="beneficiaryTextBlockParams"/>
    <!-- *********************************************
    *                  Amount Block                  *
    ********************************************** -->
    <divider-generic
    icon="bills"
    dividerSize="medium"
    :title="t('transfers.periodic.edit.step3.amountTitle')"/>
    <!-- Amount Text Block -->
    <text-block-generic
    :params="amountTextBlockParams"/>
    <!-- *********************************************
    *             Periodic Transfer Block            *
    ********************************************** -->
    <divider-generic
    icon="clock"
    dividerSize="medium"
    :title="t('transfers.periodic.edit.step3.recurrenceTitle')"/>
    <!-- Schedule Text Block -->
    <text-block-generic
    :params="scheduleTextBlockParams"/>
    <!-- Interval Text Block -->
    <text-block-generic
    v-if="editFormData.shortCode === 'V'"
    :params="intervalTextBlockParams"/>
    <!-- End Date Text Block -->
    <text-block-generic
    :params="endDateTextBlockParams"
    preventEmptyDefaultBehaviour/>
    <!-- *********************************************
    *                Description Block               *
    ********************************************** -->
    <div class="description-block">
        <divider-generic
        icon="tag"
        dividerSize="medium"
        :title="t('transfers.periodic.edit.step3.descriptionTitle')"/>
        <!-- Debit Description Text Block -->
        <text-block-generic v-if="editFormData.descriptionDebit"
        :params="debitDescriptionTextBlockParams"/>
        <!-- Credit Description Text Block -->
        <text-block-generic v-if="editFormData.descriptionCredit"
        :params="creditDescriptionTextBlockParams"/>
    </div>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import TextBlocksOperationInfo from '@/components/wrappers/OperationInfo'

/* Vue & Helpers */
import { computed } from 'vue'
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDate } from '@/assets/scripts/dateBuilders'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Props
@editFormData { Object } - object with info for our current form
@responseData { Object } - object with info from success challenge response
*/
const props = defineProps({
    editFormData: Object,
    responseData: Object
})

const operationInfoParams = computed(() => {
    if (props.responseData.errorLevel === 'INFO') {
        return {
            operationId: props.responseData.data.transfer.operationId,
            operationDate: props.responseData.data.creationDate,
            errorLevel: props.responseData.errorLevel
        }
    } else {
        return {
            operationId: props.responseData.data.operationId,
            operationDate: props.responseData.data.createDate,
            errorLevel: props.responseData.errorLevel
        }
    }
})

const originAccountTextBlockParams = {
    label: t('transfers.periodic.edit.step3.accountLabel'),
    content: props.editFormData.originAccountNumber
}

const destinationAccountTextBlockParams = {
    label: t('transfers.periodic.edit.step3.accountIBANLabel'),
    content: props.editFormData.destinationAccountNumber
}

const beneficiaryTextBlockParams = {
    label: t('transfers.periodic.edit.step3.beneficiaryLabel'),
    content: props.editFormData.beneficiaryName.toLowerCase(),
    classes: 'capitalize'
}

const amountTextBlockParams = {
    label: t('transfers.periodic.edit.step3.amountLabel'),
    content: amountFormat(props.editFormData.amount, props.editFormData.currency)
}

const scheduleTextBlockParams = {
    label: t('transfers.periodic.edit.step3.recurrenceDropdownLabel'),
    content: props.editFormData.frequencyDescription
}

const intervalTextBlockParams = {
    label: t('transfers.periodic.edit.step3.interval'),
    content: `${props.editFormData.frequency} ${t('transfers.periodic.edit.step3.days', parseInt(props.editFormData.frequency))}`
}

const endDateTextBlockParams = {
    label: props.editFormData.hasUserDefinedEndDate ? t('transfers.periodic.edit.step3.setDateRadioLabel') : t('transfers.periodic.edit.step3.noSetDateRadioLabel'),
    content: props.editFormData.hasUserDefinedEndDate ? getDate(props.editFormData.expiryDate) : '',
    classes: props.editFormData.hasUserDefinedEndDate ? 'capitalize' : null
}

const debitDescriptionTextBlockParams = {
    label: t('transfers.periodic.edit.step3.debitDescriptionLabel'),
    content: props.editFormData.descriptionDebit
}

const creditDescriptionTextBlockParams = {
    label: t('transfers.periodic.edit.step3.creditDescriptionLabel'),
    content: props.editFormData.descriptionCredit
}
</script>
<style lang="scss" scoped>
.description-block {
    width: 100%;

    .text-blocks-wrapper {
        margin-top: 16px;
    }
    .infobox-generic-wrapper {
        margin-top: 16px;
    }
}
</style>
